import React, { Suspense, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { Preloader } from '#component/preloader';
import createLabelWithIcon from '#component/icon/label-icons';
import ShipmentList from '#component/dashboard/shipment-list';
import ShipmentMap from '#component/dashboard/map/map';
import NotFoundMessage from '#component/general/not-found-message';
import { isDataExist } from '#component/dashboard/helper';
import { locationsSelector } from '#selector/shipment/overview';
import {
  DashboardWrapper,
  DashboardCnt,
  ShipmentTabs,
  BtnCnt,
  AddBtn,
  AddButtonLabel,
  TabsGrid,
  TabCnt,
  TabBar,
  TabsRoot,
  StyledTab,
  StyledDivider,
  TabContentCnt,
  MapCnt
} from '#component/dashboard/styles';

export const DesktopView = props => {
  const {
    data,
    tabNum,
    loading,
    account,
    identifiersMap
  } = props;

  const [clickedDevices, setClickedDevices] = useState([]);

  const DEFAULT_TAB = 200;
  const tab = tabNum ? parseInt(tabNum, 10) : DEFAULT_TAB;
  const { defaultDevices, states, requestStatus } = useSelector(s => s.shipment, shallowEqual);
  const shipmentLoading = requestStatus !== 'succeeded' || requestStatus === 'idle';
  const locations = locationsSelector(defaultDevices);
  const resetSelectedDevices = () => setClickedDevices([]);

  const tabProps = value => ({
    value,
    component: Link,
    to: `/dashboard/${value}`,
    replace: true
  });

  return (
    <DashboardWrapper className="dashboard">
      <DashboardCnt>
        <ShipmentTabs>
          <BtnCnt>
            {account.userType <= 20 &&
            <AddBtn
              size="small"
              color="secondary"
              aria-label="Add"
              component={Link}
              to="/addshipment"
            >
              <AddIcon />
              <AddButtonLabel>Create shipment</AddButtonLabel>
            </AddBtn>}
          </BtnCnt>
          {(!isDataExist(data) && !loading) ? (
            <NotFoundMessage>
              There are no shipments within 6 months
            </NotFoundMessage>
          ) : (
            <TabsGrid item xs={12}>
              <>
                <TabCnt>
                  <TabBar
                    position="static"
                    color="default"
                    elevation={0}
                  >
                    <TabsRoot
                      value={tab}
                      variant="scrollable"
                      scrollButtons="auto"
                      onChange={resetSelectedDevices}
                    >
                      {Object.entries(identifiersMap).map(el => {
                        const [index, identifier] = el;
                        const isActive = parseInt(index, 10) === tab;
                        const tabItemsCount = data[index]?.length || null;
                        const label = createLabelWithIcon(
                          identifier.replace(' ', '_').toUpperCase(),
                          isActive,
                          true,
                          tabItemsCount
                        );
                        return (
                          <StyledTab
                            title={identifier}
                            key={identifier}
                            label={label}
                            {...tabProps(parseInt(index, 10))}
                            className={!tabItemsCount ? 'tabEmpty' : ''}
                            wrapped
                          />
                        );
                      })}
                    </TabsRoot>
                  </TabBar>
                  <StyledDivider />
                </TabCnt>
                {identifiersMap[tab] && !loading ? (
                  <Suspense fallback={<Preloader />}>
                    <TabContentCnt square={true} elevation={0}>
                      <ShipmentList
                        tab={tab}
                        data={data[tab]}
                        account={account}
                        identifiersMap={identifiersMap}
                        clickedDevices={clickedDevices}
                        setClickedDevices={setClickedDevices}
                      />
                      <MapCnt>
                        {shipmentLoading && data[tab] ? (
                          <Preloader className="mapPreloader" />
                        ) : (
                          <ShipmentMap
                            isMobile={false}
                            zoomLastLocation={false}
                            data={data[tab]}
                            defaultDevices={defaultDevices}
                            locations={locations}
                            destinations={states}
                            clickDevices={setClickedDevices}
                            loading={shipmentLoading}
                          />
                        )}
                      </MapCnt>
                    </TabContentCnt>
                  </Suspense>
                ) : (
                  <Preloader />
                )}
              </>
            </TabsGrid>
          )}
        </ShipmentTabs>
      </DashboardCnt>
    </DashboardWrapper>
  );
};
